/**
 * Build a CSS string of @font-face rules where every `src: url(…)` has been
 * rewritten to a base64 `data:` URL, so the rules can be embedded directly
 * inside a serialized SVG and resolved by the browser when that SVG renders
 * as a `data:image/svg+xml` image.
 *
 * Why this exists: `serializeTextElementsForExport` produces inline SVGs
 * that html2canvas serializes to data URLs. The data-URL document is a
 * null-origin context with no access to the parent page's @font-face rules,
 * so it falls back to a default sans-serif — visually thinner than the
 * Inter/Poppins/Montserrat the editor renders. Embedding the actual font
 * binaries inside the SVG closes that gap.
 *
 * The result is cached in module state — the first export pays the fetch
 * cost (one Google Fonts CSS + N woff2 files), every subsequent export is
 * synchronous-fast.
 */

let cachedCss = null;
let pendingPromise = null;

async function fetchAsDataURL(url) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function buildEmbeddedFontCss() {
  const links = Array.from(
    document.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]'),
  );
  if (links.length === 0) return '';

  // Pull the CSS text for every Google Fonts <link>. fetch() respects the
  // browser's Accept header so we get the woff2 variants the page is
  // already using.
  const cssParts = await Promise.all(
    links.map(async (link) => {
      try {
        const r = await fetch(link.href, { mode: 'cors' });
        return r.ok ? r.text() : '';
      } catch {
        return '';
      }
    }),
  );
  const cssText = cssParts.join('\n');
  if (!cssText) return '';

  // Collect every distinct font binary URL referenced in the CSS.
  const urlRe = /url\(([^)]+)\)/g;
  const urlSet = new Set();
  let m;
  while ((m = urlRe.exec(cssText))) {
    const raw = m[1].trim().replace(/['"]/g, '');
    if (raw.startsWith('http')) urlSet.add(raw);
  }
  if (urlSet.size === 0) return cssText;

  // Fetch every binary in parallel, base64-encode it.
  const urlToDataUrl = new Map();
  await Promise.all(
    Array.from(urlSet).map(async (u) => {
      try {
        const dataUrl = await fetchAsDataURL(u);
        urlToDataUrl.set(u, dataUrl);
      } catch {
        /* fall through — that one @font-face rule will simply not render */
      }
    }),
  );

  // Replace each url() with its data: counterpart. We use a function
  // replacer to avoid `$` in data URLs interacting with String.replace's
  // pattern semantics.
  return cssText.replace(urlRe, (full, group) => {
    const raw = group.trim().replace(/['"]/g, '');
    const data = urlToDataUrl.get(raw);
    return data ? `url(${data})` : full;
  });
}

/**
 * Returns a Promise resolving to the embedded-font CSS string. First call
 * triggers the network fetches; subsequent calls share the same Promise (or
 * resolve synchronously once cached).
 */
export function prepareEmbeddedFontCss() {
  if (cachedCss !== null) return Promise.resolve(cachedCss);
  if (!pendingPromise) {
    pendingPromise = buildEmbeddedFontCss()
      .then((css) => {
        cachedCss = css;
        return css;
      })
      .catch(() => {
        cachedCss = '';
        return '';
      });
  }
  return pendingPromise;
}

/** Synchronous accessor — returns '' until prepareEmbeddedFontCss() resolves. */
export function getCachedEmbeddedFontCss() {
  return cachedCss || '';
}

// Split a CSS string into its individual @font-face blocks. Safe because the
// base64 data: URLs inside src:url(...) contain only [A-Za-z0-9+/=] — never a
// '}' — so a non-greedy brace match never terminates early.
function splitFontFaceBlocks(css) {
  return css.match(/@font-face\s*\{[^}]*\}/gi) || [];
}

const firstField = (block, prop) => {
  const m = new RegExp(`${prop}\\s*:\\s*([^;]+);`, 'i').exec(block);
  return m ? m[1].trim().replace(/['"]/g, '').toLowerCase() : '';
};

/**
 * Return ONLY the @font-face rules a single text element needs, keyed by its
 * primary family (and narrowed to its style/weight when those variants exist).
 *
 * Why: the full embedded CSS carries every family × every requested weight ×
 * every unicode-range subset — easily 2–3 MB of base64. Injecting all of it
 * into every per-element foreignObject SVG made html2canvas serialize tens of
 * MB and the export crawl. Each text element uses exactly one family/weight/
 * style, so we hand it just those rules (tens of KB) — correct font, fast
 * export.
 *
 * Fallbacks degrade safely: unknown family → full CSS (let the browser try);
 * no style/weight match → keep the broader family/style set rather than emit
 * nothing.
 */
export function getEmbeddedFontCssFor(family, weight, style) {
  const css = cachedCss || '';
  if (!css || !family) return css;

  const wantFamily = String(family).split(',')[0].trim().replace(/['"]/g, '').toLowerCase();
  const blocks = splitFontFaceBlocks(css);
  if (blocks.length === 0) return '';

  const famBlocks = blocks.filter((b) => firstField(b, 'font-family') === wantFamily);
  if (famBlocks.length === 0) return css; // family not in embed → don't strip it out

  const wantStyle = /italic/.test(String(style || '').toLowerCase()) ? 'italic' : 'normal';
  let out = famBlocks.filter((b) => (firstField(b, 'font-style') || 'normal') === wantStyle);
  if (out.length === 0) out = famBlocks;

  const rawWeight = String(weight || '400').trim().toLowerCase();
  const wantWeight = rawWeight === 'normal' ? '400' : rawWeight === 'bold' ? '700' : rawWeight;
  const byWeight = out.filter((b) => (firstField(b, 'font-weight') || '400') === wantWeight);
  if (byWeight.length > 0) out = byWeight;

  return out.join('\n');
}
